/**
 * CAPACITY — GET /v1/pools, the per-pool view of how close a MID/STC is to its ceiling.
 *
 * WHY THIS TAB EXISTS: capacity in this system is per (channel, MID width, serial width, MID, STC)
 * pool and it does NOT aggregate. Until this endpoint existed, the first time anyone learned a
 * combination was near its ceiling was a 429 EXHAUSTED — i.e. after it had already started
 * refusing label requests. That is not monitoring, and it is the answer to "how do we survive a
 * region outage without exhausting barcodes": you cannot move a customer's construct, so the
 * minimum viable answer is knowing which pools are at risk, early.
 *
 * ⚠️ THIS TAB IS A READ, NOT A VEND. It allocates nothing and burns no serials, so it is safe to
 * call from a button. That is the opposite of the load-test tab and the difference is not
 * cosmetic — a read costs nothing a manual press would save.
 *
 * ⚠️ AND IT MEASURES ONE OF TWO FAILURE MODES. It measures EXHAUSTION (running out of numbers,
 * months) and NOT refill saturation (running out of refill throughput, seconds). The endpoint says
 * so in its own payload rather than leaving it to inference, and this panel renders that statement
 * rather than paraphrasing it.
 */

import { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Table from '@cloudscape-design/components/table';
import Badge from '@cloudscape-design/components/badge';
import Button from '@cloudscape-design/components/button';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import RawJson from './RawJson.jsx';
import { timedFetch, errorText, isPlaceholder } from '../lib/api.js';
import { POOLS_UNPROVEN, POOLS_RATE_IS_LIFETIME_MEAN, NO_OUTAGE_DRILL } from '../lib/caveats.js';

/** Status to a Cloudscape indicator type. An unrecognised status renders VERBATIM, never as OK. */
const STATUS_TYPE = {
  OK: 'success',
  WATCH: 'info',
  DANGER: 'warning',
  CRITICAL: 'error',
  EXHAUSTED: 'error',
  NO_MARK: 'pending',
  UNRESOLVABLE: 'stopped',
};

const pct = (v) => (v === null || v === undefined ? 'n/a' : `${v}%`);
const int = (v) => (v === null || v === undefined ? 'n/a' : Number(v).toLocaleString('en-US'));

export default function CapacityPanel({ endpoints, live, liveProbe }) {
  const [poolId, setPoolId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function load(explicitPool) {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      if (!live) {
        throw new Error(
          'Per-pool capacity is a live read of the running service’s marks and ranges. There is no ' +
            'local stand-in, because a fabricated utilisation figure is worse than none.' +
            (liveProbe && liveProbe.reason ? ` ${liveProbe.reason}` : '')
        );
      }
      const p = (explicitPool === undefined ? poolId : explicitPool).trim();
      const qs = p ? `?pool=${encodeURIComponent(p)}` : '';
      const res = await timedFetch(`${endpoints.serialApi}/v1/pools${qs}`, {}, 15000);
      if (!res.ok) throw new Error(errorText(res));
      const payload = res.json || {};
      if (isPlaceholder(payload)) {
        throw new Error(
          'the endpoint answered with placeholder: true, so no mark and no range was read — an ' +
            'empty pool list from a placeholder is not "no pools at risk"'
        );
      }
      setResult({ ms: res.ms, payload, queriedPool: p || null });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const payload = result && result.payload;
  const pools = (payload && Array.isArray(payload.pools) ? payload.pools : []) || [];
  const summary = (payload && payload.summary) || {};
  const coverage = (payload && payload.coverage) || {};
  const measures = (payload && payload.measures) || {};

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description="GET /v1/pools — each pool's mark position against its region's half of the serial range, read live. A read only: it allocates nothing and burns no serials."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button loading={busy} onClick={() => load('')} data-testid="capacity-list">
                  List registered pools
                </Button>
              </SpaceBetween>
            }
          >
            Per-pool capacity
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p">
            <strong>Capacity does not aggregate.</strong> Every{' '}
            <em>(Mailer ID, Service Type Code)</em> combination has its own finite pool, and each
            region owns a disjoint half of it. A deployment with plenty of room overall can still have
            one combination about to start refusing requests — so there is no single
            &ldquo;capacity&rdquo; number to watch, and this is the per-pool view instead.
          </Box>

          <form onSubmit={(e) => { e.preventDefault(); load(); }}>
            <Form
              actions={
                <Button variant="primary" loading={busy} onClick={() => load()} data-testid="capacity-submit">
                  Assess this pool
                </Button>
              }
              errorText={error}
            >
              <FormField
                label="Pool id"
                description="channelAI-midLength-serialLength-MID-STC, for example 92-9-7-123456789-123. Leave the field empty and press “List registered pools” to page the registry instead."
              >
                <Input
                  value={poolId}
                  onChange={({ detail }) => setPoolId(detail.value)}
                  placeholder="92-9-7-123456789-123"
                  data-testid="capacity-pool"
                />
              </FormField>
            </Form>
          </form>
        </SpaceBetween>
      </Container>

      {/*
        THE TWO CAVEATS THAT CHANGE WHAT THIS TAB MEANS, above the numbers rather than below them.
        A reader who stops after the table must have seen both.
      */}
      <Alert type="warning" header="What this tab can and cannot see">
        <SpaceBetween size="xs">
          <Box variant="p" data-testid="capacity-unproven">
            {POOLS_UNPROVEN}
          </Box>
          <Box variant="p" data-testid="capacity-rate">
            {POOLS_RATE_IS_LIFETIME_MEAN}
          </Box>
          <Box variant="p">
            It measures <strong>exhaustion</strong> — running out of numbers, which is a
            months-scale problem visible as the mark approaching the range end. It does{' '}
            <strong>not</strong> measure <strong>refill saturation</strong> — running out of refill
            throughput, which happens in seconds. Those are independent, so{' '}
            <strong>a pool can be refusing requests right now while its utilisation below looks
            healthy.</strong>
          </Box>
          <Box variant="p">{NO_OUTAGE_DRILL}</Box>
        </SpaceBetween>
      </Alert>

      {result && (
        <SpaceBetween size="l">
          <Container
            header={
              <Header
                variant="h2"
                description="Rolled up from the assessed pools. Anything unassessable is counted separately and never added to OK — an unassessed pool is not a healthy one."
              >
                Summary
              </Header>
            }
          >
            <SpaceBetween size="m">
              <ColumnLayout columns={4} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Pools assessed</Box>
                  <Box variant="h1" fontSize="display-l" data-testid="capacity-assessed">
                    {int(summary.assessed)}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    region <code>{payload.regionId || 'unknown'}</code> · read in{' '}
                    {result.ms.toFixed(0)} ms
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Worst status</Box>
                  <Box variant="h1" fontSize="display-l">
                    <StatusIndicator type={STATUS_TYPE[summary.worstStatus] || 'stopped'}>
                      {summary.worstStatus || 'n/a'}
                    </StatusIndicator>
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    needing attention: {int(summary.poolsNeedingAttention)}
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">In the region-failure danger zone</Box>
                  <Box variant="h1" fontSize="display-l" data-testid="capacity-danger">
                    {int(summary.poolsInDangerZone)}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    pools whose traffic is above half their both-region ceiling
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Rate unknown</Box>
                  <Box variant="h1" fontSize="display-l">
                    {int(summary.poolsWithUnknownRate)}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    no registry entry, so no runway can be computed
                  </Box>
                </div>
              </ColumnLayout>

              <Alert type="info" header="The danger zone, and why it is a property of the traffic rather than of the mark">
                <SpaceBetween size="xs">
                  <Box variant="p">
                    Let <strong>u</strong> be a combination’s total daily issuance divided by its
                    pool’s both-region 120-day ceiling. A surviving region exhausts only if an outage
                    lasts longer than <Box variant="code">120 × (1−u) / u</Box> days and shorter than
                    about 128 days.{' '}
                    <strong>If u is at or below 0.5 that window is EMPTY and no outage of any length
                    exhausts the pool.</strong> On a 7-digit serial that threshold is 20,833 per day
                    per region.
                  </Box>
                  <Box variant="p">
                    Short outages are harmless and very long ones self-heal, because after about 120
                    days the serials ageing out were issued <em>during</em> the outage at the
                    doubled rate — so the survivor stabilises. The dangerous outage is a medium one,
                    and how medium depends entirely on u.
                  </Box>
                  <Box variant="p">
                    <strong>That is why the alarm is on u and not on the mark.</strong> A rate alarm
                    fires months ahead; a mark alarm fires when the space is nearly gone. Minting a
                    fresh Mailer ID — a fresh pool, disjoint by construction — is the one capacity
                    lever available inside a construct the customer already chose, and above u ≈ 0.95
                    an outage does not leave enough time to do it.
                  </Box>
                </SpaceBetween>
              </Alert>

              <Alert type="warning" header="Exhaustion is safe and scoped, which is the property never to trade away">
                <Box variant="p">
                  A region <strong>physically cannot</strong> allocate past its ceiling: the mark
                  update carries a condition that refuses it. So exhaustion is a hard refusal on{' '}
                  <strong>one</strong> MID/STC combination — visibly bad rather than silently wrong.
                  There is no wrap and no chance of reusing a number. Never trade that for capacity.
                </Box>
              </Alert>
            </SpaceBetween>
          </Container>

          <Container
            header={
              <Header
                variant="h2"
                counter={`(${pools.length})`}
                description="One row per pool. Utilisation is the mark against this region's half of the range; the danger zone and the runway need a rate, and say so when there is none."
              >
                Pools
              </Header>
            }
          >
            <SpaceBetween size="m">
              <Table
                variant="embedded"
                contentDensity="compact"
                wrapLines
                data-testid="capacity-table"
                trackBy="poolId"
                items={pools}
                empty={
                  <Box variant="p" padding="s">
                    No pools returned. For the listing that is <strong>not</strong> the same as “no
                    pools exist” — the registry is still a lower bound. A pool is registered when
                    it <strong>refills a block</strong>, so one vended only through the A6 buffer
                    bypass (<code>X-Bypass-Buffer</code>), which never refills, is absent from it.
                    Name a pool id directly to assess it regardless of registration.
                  </Box>
                }
                columnDefinitions={[
                  {
                    id: 'pool',
                    header: 'Pool',
                    cell: (p) => (
                      <SpaceBetween size="xxs">
                        <Box fontWeight="bold">
                          <code>{p.poolId}</code>
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          MID {p.mid} · STC {p.stc} ·{' '}
                          {(p.constructsSharingThisPool || []).join(' / ') || 'unknown constructs'}
                        </Box>
                      </SpaceBetween>
                    ),
                    width: 300,
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    cell: (p) => (
                      <SpaceBetween size="xxs">
                        <StatusIndicator type={STATUS_TYPE[p.status] || 'stopped'}>
                          {p.status}
                        </StatusIndicator>
                        {(p.reasons || []).map((r, i) => (
                          <Box key={i} variant="small" color="text-body-secondary">
                            {r}
                          </Box>
                        ))}
                      </SpaceBetween>
                    ),
                    width: 240,
                  },
                  {
                    id: 'util',
                    header: 'Utilisation',
                    cell: (p) => <strong>{pct(p.utilisationPct)}</strong>,
                    width: 120,
                  },
                  { id: 'remaining', header: 'Remaining', cell: (p) => int(p.remaining), width: 130 },
                  {
                    id: 'u',
                    header: 'u (rate ÷ ceiling)',
                    cell: (p) =>
                      p.u === null || p.u === undefined ? (
                        <Box variant="small" color="text-body-secondary">
                          not computable — {p.rateBasis || 'no rate'}
                        </Box>
                      ) : (
                        <strong>{p.u}</strong>
                      ),
                    width: 200,
                  },
                  {
                    id: 'zone',
                    header: 'Danger zone',
                    cell: (p) =>
                      !p.dangerZone ? (
                        '—'
                      ) : p.dangerZone.assessable === false ? (
                        <Box variant="small" color="text-body-secondary">
                          not assessable — {p.dangerZone.reason}
                        </Box>
                      ) : p.dangerZone.open ? (
                        <Badge color="red">
                          open from day {p.dangerZone.opensAtDay} to{' '}
                          {p.dangerZone.closesAtDayMax}
                        </Badge>
                      ) : (
                        <Badge color="green">empty — no outage exhausts this pool</Badge>
                      ),
                    minWidth: 220,
                  },
                  {
                    id: 'refill',
                    header: 'Refill pressure',
                    cell: (p) =>
                      p.refillPressure && p.refillPressure.available === true ? (
                        <Box>{String(p.refillPressure.cyclesPerSec ?? 'reported')}</Box>
                      ) : (
                        <Box variant="small" color="text-status-warning">
                          NOT MEASURED
                        </Box>
                      ),
                    width: 150,
                  },
                  {
                    id: 'registered',
                    header: 'In the registry',
                    cell: (p) =>
                      p.registered ? (
                        <Badge color="green">yes</Badge>
                      ) : (
                        <Badge color="grey">no — assessed directly</Badge>
                      ),
                    width: 170,
                  },
                ]}
              />

              <KeyValuePairs
                columns={2}
                items={[
                  {
                    label: 'What is measured',
                    value: <Box variant="small">{measures.exhaustionRisk || 'not reported'}</Box>,
                  },
                  {
                    label: 'What is NOT measured',
                    value: <Box variant="small">{measures.refillSaturation || 'not reported'}</Box>,
                  },
                  {
                    label: 'Coverage of the listing',
                    value: (
                      <Box variant="small">
                        <strong>{coverage.completeness || 'unknown'}</strong>
                        {coverage.why ? ` — ${coverage.why}` : ''}
                      </Box>
                    ),
                  },
                  {
                    label: 'How the rate is derived',
                    value: <Box variant="small">{measures.rate || 'not reported'}</Box>,
                  },
                ]}
              />

              {payload.truncated && (
                <Alert type="info" header="This is one page, and the endpoint says so rather than truncating silently">
                  <Box variant="p">
                    More registered pools exist beyond this page. A silently short list is a silently
                    unmonitored pool, so the response carries an explicit paging key instead of
                    stopping quietly.
                  </Box>
                </Alert>
              )}

              <RawJson label="Raw /v1/pools response" value={payload} />
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      )}

      {error && !result && (
        <Alert type="error" header="Per-pool capacity could not be read, and nothing is being substituted for it">
          {error}
        </Alert>
      )}
    </SpaceBetween>
  );
}
